<script setup lang="ts">
import { computed, shallowRef, watch } from 'vue';
import { Moon, Plus, Sun, Unplug } from 'lucide-vue-next';
import { useDark, useToggle } from '@vueuse/core';
import ConnectForm from '@/components/ConnectForm.vue';
import WorkspaceLayout from '@/components/WorkspaceLayout.vue';
import { Button } from '@/components/ui/button';
import { useSsh, type ConnectionConfig } from '@/composables/use-ssh';
import {
	useSftp,
	type ExpandedPathStore,
	type RootPathStore,
	type FileNode,
} from '@/composables/use-sftp';
import {
	useWorkspace,
	type OpenPanelsState,
	type PersistedSplitNode,
} from '@/composables/use-workspace';
import {
	usePanels,
	createLeaf,
	collectPanels,
	type PanelRegion,
	type SplitNode,
} from '@/composables/use-panels';
import { provideWorkspace, type WorkspaceContext } from '@/composables/use-workspace-context';
import { createFileOpener } from '@/openers/file-opener.js';
import { createMediaOpener } from '@/openers/media-opener.js';
import { createTerminalOpener } from '@/openers/terminal-opener.js';
import { createFileTreeOpener } from '@/openers/file-tree-opener.js';
import type { SelectionState } from '@/composables/use-virtual-list';

const { state, error, hasShell, hasSftp, client, connect, openShell, disconnect } = useSsh();

let terminalCounter = 0;

const connectionConfig = shallowRef<ConnectionConfig | null>(null);
const workspace = useWorkspace(connectionConfig);

// --- panel-scoped persistence for the file tree ---

const FILE_TREE_URI = 'file-tree://explorer';

type FileTreePanelState = {
	expandedPaths: string[];
	selectedPaths: string[];
	rootPath: string | null;
};

const getFileTreeState = (): FileTreePanelState => {
	return (
		workspace.getPanelState<FileTreePanelState>(FILE_TREE_URI) ?? {
			expandedPaths: [],
			selectedPaths: [],
			rootPath: null,
		}
	);
};

const updateFileTreeState = (patch: Partial<FileTreePanelState>) => {
	const current = getFileTreeState();
	workspace.setPanelState(FILE_TREE_URI, { ...current, ...patch });
};

const expandedStore: ExpandedPathStore = {
	addExpandedPath(path: string) {
		const s = getFileTreeState();
		if (!s.expandedPaths.includes(path)) {
			updateFileTreeState({ expandedPaths: [...s.expandedPaths, path] });
		}
	},
	removeExpandedPath(path: string) {
		const s = getFileTreeState();
		updateFileTreeState({ expandedPaths: s.expandedPaths.filter((p) => p !== path) });
	},
	isExpanded(path: string): boolean {
		return getFileTreeState().expandedPaths.includes(path);
	},
};

const rootPathStore: RootPathStore = {
	getRootPath(): string | null {
		return getFileTreeState().rootPath;
	},
	setRootPath(path: string) {
		updateFileTreeState({ rootPath: path });
	},
};

const selectionState: SelectionState = {
	getSelectedKeys(): string[] {
		return getFileTreeState().selectedPaths;
	},
	setSelectedKeys(keys: string[]) {
		updateFileTreeState({ selectedPaths: keys });
	},
};

const {
	rootNode,
	rootPath,
	openFiles,
	activeFilePath,
	initializing,
	error: sftpError,
	toggleDirectory,
	readFile,
	openFilePath: sftpOpenFilePath,
	changeRoot,
	updateFileContent,
	saveFile,
	closeFile: sftpCloseFile,
	clearError,
} = useSftp(client, expandedStore, rootPathStore);

const isDark = useDark();
const toggleDark = useToggle(isDark);

const panels = usePanels();

const isConnected = computed(() => state.value === 'connected');
const isConnecting = computed(() => state.value === 'connecting');

// --- panel openers ---

panels.registerOpener(createFileOpener({ closeFile: sftpCloseFile }));
panels.registerOpener(createMediaOpener());
panels.registerOpener(createTerminalOpener());
panels.registerOpener(createFileTreeOpener());

// --- wrap openFile to integrate with panel system ---

const openFile = (node: FileNode) => {
	if (node.isDirectory) return;
	panels.open(`file://${node.path}`);
	persistPanels();
};

// --- wrap closeFile to clean up via panel system ---

const closeFile = (path: string) => {
	panels.close(`file://${path}`);
	persistPanels();
};

// keep activeFilePath in sync with focused center panel
watch(
	() => panels.activeUriForRegion('center'),
	(uri) => {
		if (uri && uri.startsWith('file://')) {
			activeFilePath.value = uri.slice('file://'.length);
		}
	},
);

// persist panel layout whenever active tabs change in any region
watch(
	() =>
		(['left', 'center', 'right', 'bottom'] as PanelRegion[]).map((r) =>
			panels.activeUriForRegion(r),
		),
	() => persistPanels(),
);

// persist panel layout on tree structure / resize changes
watch(
	() => panels.treeVersion.value,
	() => persistPanels(),
);

// --- provide workspace context for panel components ---

const ctx: WorkspaceContext = {
	panels,
	client,
	hasShell,
	hasSftp,
	openShell,
	disconnect,
	rootNode,
	rootPath,
	initializing,
	sftpError: sftpError,
	selectionState,
	toggleDirectory,
	openFile,
	changeRoot,
	clearError,
	readFile,
	openFiles,
	activeFilePath,
	openFilePath: sftpOpenFilePath,
	updateFileContent,
	saveFile,
	closeFile,
	getPanelState: workspace.getPanelState,
	setPanelState: workspace.setPanelState,
	layout: workspace.layout,
};

provideWorkspace(ctx);

// --- serialize split tree for persistence ---

const serializeTree = (node: SplitNode): PersistedSplitNode => {
	if (node.type === 'leaf') {
		return { type: 'leaf', id: node.id };
	}
	return {
		type: 'branch',
		id: node.id,
		direction: node.direction,
		children: node.children.map(serializeTree),
		sizes: [...node.sizes],
	};
};

// deserialize a persisted tree back to a live tree (panels are added separately)
const deserializeTree = (persisted: PersistedSplitNode): SplitNode => {
	if (persisted.type === 'leaf') {
		return createLeaf(persisted.id);
	}
	return {
		type: 'branch',
		id: persisted.id,
		direction: persisted.direction,
		children: persisted.children.map(deserializeTree),
		sizes: [...persisted.sizes],
	};
};

// --- persist and restore open panels ---

const persistPanels = () => {
	const allPanels: OpenPanelsState['panels'] = [];
	const activeUris: Partial<Record<PanelRegion, string>> = {};
	const trees: Partial<Record<PanelRegion, PersistedSplitNode>> = {};

	for (const [region, regionRoot] of Object.entries(panels.regions.value) as [
		PanelRegion,
		{ root: SplitNode },
	][]) {
		const regionPanels = collectPanels(regionRoot.root);
		for (const panel of regionPanels) {
			allPanels.push({ uri: panel.uri, region: panel.region, nodeId: panel.nodeId });
		}

		const active = panels.activeUriForRegion(region as PanelRegion);
		if (active) {
			activeUris[region as PanelRegion] = active;
		}

		// only persist tree structure if it has splits (branches)
		if (regionRoot.root.type === 'branch') {
			trees[region as PanelRegion] = serializeTree(regionRoot.root);
		}
	}

	workspace.openPanels.value = { panels: allPanels, activeUris, trees };
};

// --- connection lifecycle ---

const onConnect = (config: ConnectionConfig) => {
	connectionConfig.value = config;
	connect(config);
};

const onDisconnect = () => {
	disconnect();
};

const openNewTerminal = () => {
	terminalCounter++;
	panels.open(`terminal://${terminalCounter}`, { region: 'bottom' });
	persistPanels();
};

// when connected, restore persisted panels (or open defaults)
watch(isConnected, (connected) => {
	if (connected) {
		const persisted = workspace.openPanels.value;

		if (persisted.panels.length > 0) {
			// restore split trees first (before opening panels into them)
			if (persisted.trees) {
				for (const [region, tree] of Object.entries(persisted.trees) as [
					PanelRegion,
					PersistedSplitNode,
				][]) {
					if (tree) {
						panels.regions.value[region].root = deserializeTree(tree);
					}
				}
			}

			// restore all persisted panels — track highest terminal id
			for (const entry of persisted.panels) {
				panels.open(entry.uri, {
					region: entry.region,
					nodeId: entry.nodeId,
					focus: false,
				});
				if (entry.uri.startsWith('terminal://')) {
					const id = parseInt(entry.uri.split('//')[1] ?? '0', 10);
					if (id >= terminalCounter) terminalCounter = id + 1;
				}
			}

			// restore active tabs per region
			for (const [, uri] of Object.entries(persisted.activeUris)) {
				if (uri) {
					panels.focus(uri);
				}
			}
		} else {
			// first connection — open defaults based on capabilities
			if (hasSftp.value) {
				panels.open('file-tree://explorer', { region: 'left' });
			}
			if (hasShell.value) {
				terminalCounter++;
				panels.open(`terminal://${terminalCounter}`, { region: 'bottom' });
			}
			persistPanels();
		}
	} else {
		// clear all panels on disconnect
		terminalCounter = 0;
		for (const regionRoot of Object.values(panels.regions.value)) {
			const allRegionPanels = collectPanels(regionRoot.root);
			for (const panel of [...allRegionPanels]) {
				panels.close(panel.uri);
			}
		}
	}
});
</script>

<template>
	<!-- connect form view -->
	<div v-if="!isConnected" class="relative flex h-screen items-center justify-center p-4">
		<Button variant="ghost" size="icon" class="absolute right-4 top-4" @click="toggleDark()">
			<Sun v-if="isDark" class="size-5" />
			<Moon v-else class="size-5" />
		</Button>
		<ConnectForm :loading="isConnecting" :error="error" @connect="onConnect" />
	</div>

	<!-- connected workspace -->
	<div v-else class="flex h-screen flex-col overflow-hidden">
		<!-- header bar -->
		<header class="flex h-10 shrink-0 items-center gap-2 px-3">
			<div class="flex flex-1 items-center gap-2 overflow-hidden">
				<span class="text-sm text-muted-foreground">
					{{ connectionConfig?.user }}@{{ connectionConfig?.hostname }}
				</span>
			</div>

			<!-- sftp error -->
			<span v-if="sftpError" class="text-xs text-destructive">
				{{ sftpError }}
				<button class="ml-1 underline" @click="clearError">dismiss</button>
			</span>

			<Button
				v-if="hasShell"
				variant="ghost"
				size="sm"
				class="h-7 gap-1.5 text-xs"
				@click="openNewTerminal"
			>
				<Plus class="size-3.5" />
				Terminal
			</Button>

			<Button variant="ghost" size="icon" class="size-7" @click="toggleDark()">
				<Sun v-if="isDark" class="size-4" />
				<Moon v-else class="size-4" />
			</Button>

			<Button variant="ghost" size="sm" class="h-7 gap-1.5 text-xs" @click="onDisconnect">
				<Unplug class="size-3.5" />
				Disconnect
			</Button>
		</header>

		<!-- panel layout -->
		<WorkspaceLayout :panels="panels" />
	</div>
</template>
